import { Space, Typography, Tag, Button, Table, Select, Avatar, Card, Drawer, Form, Input, Slider, DatePicker, Spin, Alert } from 'antd';
import { UserOutlined, MoreOutlined, FilterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '../utils';
import { useDashboardMerchants } from '../hooks/useDashboardQueries';
import { useState } from 'react';

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const MerchantBillingTable = ({ dateRange, isFilterDrawerOpen, setIsFilterDrawerOpen }: any) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('all');

    const params = {
        startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
        timezone: 'Asia/Kolkata',
        page,
        limit: 10,
        status: statusFilter !== 'all' ? statusFilter : undefined
    };

    const { data: response, isLoading, isError } = useDashboardMerchants(params);

    if (isLoading) return <Card title="Merchant Billing Overview" style={{ height: '100%' }}><Spin size="large" style={{ display: 'block', margin: 'auto', marginTop: 50 }} /></Card>;
    if (isError) return <Alert type="error" message="Failed to load merchant data" />;

    const merchants = response?.data || [];
    const paginationMeta = response?.meta?.pagination || { page: 1, limit: 10, total: 0 };

    const columns = [
        {
            title: 'Merchant',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: any) => (
                <Space>
                    <Avatar style={{ backgroundColor: record.avatarColor }}>{text?.charAt(0) || 'M'}</Avatar>
                    <Space direction="vertical" size={0}>
                        <Text strong style={{ fontSize: 13 }}>{text}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
                    </Space>
                </Space>
            )
        },
        {
            title: 'Wallet Balance',
            key: 'walletBalance',
            render: (_: any, record: any) => <Text style={{ fontSize: 13 }}>₹{formatMoney(record?.snapshotMetrics?.walletBalance)}</Text>
        },
        {
            title: 'Commission',
            key: 'commission',
            render: (_: any, record: any) => <Text style={{ fontSize: 13 }}>₹{formatMoney(record?.periodMetrics?.commission?.chargedAmount)}</Text>
        },
        {
            title: 'Radius',
            key: 'radius',
            render: (_: any, record: any) => <Text style={{ fontSize: 13 }}>₹{formatMoney(record?.periodMetrics?.radiusCharges?.chargedAmount)}</Text>
        },
        {
            title: 'Users',
            key: 'users',
            render: (_: any, record: any) => <Text style={{ fontSize: 13 }}>₹{formatMoney(record?.periodMetrics?.userCharges?.chargedAmount)}</Text>
        },
        {
            title: 'Upcoming Charges',
            key: 'upcomingCharges',
            render: (_: any, record: any) => <Text style={{ fontSize: 13 }}>₹{formatMoney(record?.snapshotMetrics?.upcomingCharges)}</Text>
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: any, record: any) => {
                const status = record?.snapshotMetrics?.walletStatus || 'UNKNOWN';
                let color = 'green';
                let bgColor = '#f6ffed';
                if (status === 'LOW_BALANCE') { color = 'orange'; bgColor = '#fff7e6'; }
                if (status === 'INSUFFICIENT') { color = 'red'; bgColor = '#fff2f0'; }
                return <Tag color={color} style={{ border: 'none', background: bgColor, borderRadius: 12 }}>{status}</Tag>
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: () => (
                <Space size="middle">
                    <Button type="text" icon={<UserOutlined />} size="small" />
                    <Button type="text" icon={<MoreOutlined />} size="small" />
                </Space>
            )
        }
    ];

    return (
        <>
            <Card
                title="Merchant Billing Overview"
                extra={
                    <Space>
                        <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 140 }} options={[{ value: 'all', label: 'All Status' }, { value: 'HEALTHY', label: 'Healthy' }, { value: 'LOW_BALANCE', label: 'Low Balance' }]} />
                        <Button icon={<FilterOutlined />} onClick={() => setIsFilterDrawerOpen(true)}>Filters</Button>
                        <a href="#" onClick={(e) => { e.preventDefault(); navigate('/merchants'); }}>View All</a>
                    </Space>
                }
                styles={{ body: { padding: 0 } }}
            >
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={merchants}
                    pagination={{ 
                        current: paginationMeta.page,
                        pageSize: paginationMeta.limit,
                        total: paginationMeta.total,
                        onChange: (p) => setPage(p),
                        position: ['bottomCenter'], 
                        size: 'small', 
                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} results` 
                    }}
                    size="middle"
                />
            </Card>
            <Drawer
                title="Filter Merchants"
                placement="right"
                onClose={() => setIsFilterDrawerOpen(false)}
                open={isFilterDrawerOpen}
                footer={
                    <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={() => setIsFilterDrawerOpen(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => setIsFilterDrawerOpen(false)}>
                            Apply Filters
                        </Button>
                    </Space>
                }
            >
                <Form layout="vertical">
                    <Form.Item label="Search">
                        <Input placeholder="Search by name or email" />
                    </Form.Item>
                    <Form.Item label="Status">
                        <Select
                            placeholder="Select status"
                            options={[
                                { value: 'active', label: 'Active' },
                                { value: 'low_balance', label: 'Low Balance' },
                                { value: 'insufficient', label: 'Insufficient' },
                                { value: 'suspended', label: 'Suspended' },
                            ]}
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item label="Wallet Balance Range">
                        <Slider range defaultValue={[0, 100000]} max={500000} step={1000} />
                    </Form.Item>
                    <Form.Item label="Registration Date">
                        <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
};
